import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    CircularProgress,
    Button,
    Typography,
    Menu,
    MenuItem,
    IconButton
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
// import PhoneIcon from '@material-ui/icons/Phone'; // reserved for isOnCall feature
import { withStyles, makeStyles } from '@material-ui/styles';
import moment from 'moment';

import AppContext from '../../../../App/AppContext';
import { SelectBox } from '../../../../Core/FormInput';
import BreadCrumbs from '../../../../Core/Controls/Breadcrumb';
import DialogComp from '../../../../Core/Controls/Dialog/DialogComp';
import { getBranches } from '../../../../Core/Service/branches-service';
import { getWorkshopShiftWeeklyData, getWorkshopShiftsTechniciansByBranch } from '../../../../Core/Service/WorkshopShiftSchedule-service';
import CreateCalendarModal from './CreateCalendarModal';
import AssignTechnicianModal from './AssignTechnicianModal';
import UnassignTechnicianModal from './UnassignTechnicianModal';
import AddHolidayModal from './AddHolidayModal';

// ─── Styled TableCell ────────────────────────────────────────────────────────
const HC = withStyles(() => ({
    head: {
        backgroundColor: '#fff',
        color: '#6B7280',
        fontWeight: 600,
        fontSize: 12,
        padding: '8px 10px',
        borderBottom: '1px solid #E5E7EB',
        borderRight: '1px solid #F3F4F6',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
        '&:last-child': { borderRight: 'none' }
    },
    body: {
        fontSize: 13,
        padding: '6px 10px',
        borderRight: '1px solid #F3F4F6',
        borderBottom: '1px solid #F3F4F6',
        verticalAlign: 'middle',
        '&:last-child': { borderRight: 'none' }
    }
}))(TableCell);

// ─── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles(() => ({
    root: {
        backgroundColor: '#F9FAFB',
        minHeight: '100vh',
        padding: '0'
    },
    topBar: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        backgroundColor: '#fff',
        borderBottom: '1px solid #E5E7EB',
        flexWrap: 'wrap'
    },
    filterLabel: {
        fontSize: 11,
        fontWeight: 600,
        color: '#9CA3AF',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 3,
        display: 'block',
        textAlign: 'left'
    },
    filterWrap: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 160
    },
    createBtn: {
        height: 36,
        fontSize: 13,
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: 6,
        padding: '0 16px',
        backgroundColor: '#2563EB',
        color: '#fff',
        boxShadow: 'none',
        marginLeft: 'auto',
        '&:hover': { backgroundColor: '#1D4ED8', boxShadow: 'none' }
    },
    tableWrap: {
        margin: '16px',
        border: '1px solid #E5E7EB',
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: '#fff'
    },

    // Pill inside shift day cell
    shiftPill: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 5,
        padding: '3px 10px',
        borderRadius: 5,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        cursor: 'pointer',
        border: '1px solid transparent',
        transition: 'opacity 0.15s',
        '&:hover': { opacity: 0.82 }
    },
    // Empty schedule cell hover area
    emptyCell: {
        height: 32,
        borderRadius: 5,
        cursor: 'pointer',
        transition: 'background 0.15s',
        '&:hover': { backgroundColor: '#EFF6FF' }
    },
    // Technician row
    techRow: {
        '& td': {
            padding: '5px 10px',
            borderBottom: '1px solid #F3F4F6'
        }
    },
    // Assigned tech chip in day cell
    techChip: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 8px',
        borderRadius: 4,
        backgroundColor: '#F3F4F6',
        color: '#374151',
        fontSize: 12,
        fontWeight: 500,
        cursor: 'pointer',
        border: '1px solid #E5E7EB',
        transition: 'background 0.15s',
        '&:hover': { backgroundColor: '#E5E7EB' }
    },
    onCallDot: {
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: '#F59E0B',
        display: 'inline-block',
        marginLeft: 3
    },
    // Assign tech link in first column
    assignLink: {
        fontSize: 12,
        color: '#2563EB',
        fontWeight: 500,
        cursor: 'pointer',
        '&:hover': { textDecoration: 'underline' }
    },
    // Tech name in first column
    techName: {
        fontSize: 13,
        color: '#111827',
        fontWeight: 500,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        '&:hover': { color: '#2563EB' }
    },
    emptyState: {
        padding: '60px 20px',
        textAlign: 'center'
    }
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────
const crumbs = [
    { name: 'Home', path: '/' },
    { name: 'T-Card', path: '/' },
    { name: 'Workshop Shift Schedule', path: '/', active: true }
];

const formatTechName = (techID, techName, techList = []) => {
    if (techID) {
        const matched = techList.find((t) => String(t.id).toLowerCase() === String(techID).toLowerCase());
        if (matched) return matched.name;
    }
    if (!techName) return '';
    const part = techName.split('@')[0];
    return part
        .split(/[._-]/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
};

// ─── Component ────────────────────────────────────────────────────────────────
const WorkshopShiftSchedule = () => {
    const classes = useStyles();
    const { showToast } = useContext(AppContext);

    const [branchList, setBranchList] = useState([]);
    const [branchID, setBranchID] = useState('');
    const [year, setYear] = useState(moment().year());
    const [week, setWeek] = useState(moment().isoWeek());

    const [weeklyData, setWeeklyData] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [technicianList, setTechList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isBranchLoading, setIsBranchLoading] = useState(true);
    const [isTechLoading, setIsTechLoading] = useState(false);

    const [calendarOpen, setCalendarOpen] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [assignOpen, setAssignOpen] = useState(false);
    const [assignParams, setAssignParams] = useState(null);
    const [unassignOpen, setUnassignOpen] = useState(false);
    const [unassignParams, setUnassignParams] = useState(null);
    const [holidayOpen, setHolidayOpen] = useState(false);
    const [holidayParams, setHolidayParams] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [activeMenuAssignment, setActiveMenuAssignment] = useState(null);

    const handlePrevWeek = () => {
        let newWeek = week - 1;
        let newYear = year;
        if (newWeek < 1) {
            newYear = year - 1;
            newWeek = moment().year(newYear).isoWeeksInYear();
        }
        setYear(newYear);
        setWeek(newWeek);
    };

    const handleNextWeek = () => {
        let newWeek = week + 1;
        let newYear = year;
        const totalWeeks = moment().year(year).isoWeeksInYear();
        if (newWeek > totalWeeks) {
            newYear = year + 1;
            newWeek = 1;
        }
        setYear(newYear);
        setWeek(newWeek);
    };

    // Year list
    const yearList = useMemo(() => {
        const cy = moment().year();
        return Array.from({ length: 5 }, (_, i) => ({ id: cy - 2 + i, name: String(cy - 2 + i) }));
    }, []);

    // Week list
    const weekList = useMemo(() => {
        const total = moment().year(year).isoWeeksInYear();
        return Array.from({ length: total }, (_, i) => {
            const w = i + 1;
            const s = moment().year(year).isoWeek(w).startOf('isoWeek');
            const e = moment().year(year).isoWeek(w).endOf('isoWeek');
            return { id: w, name: `Week ${w} (${s.format('DD/MM/YYYY')} - ${e.format('DD/MM/YYYY')})` };
        });
    }, [year]);

    // Days of selected week
    const daysOfWeek = useMemo(() => {
        const start = moment().year(year).isoWeek(week).startOf('isoWeek');
        return Array.from({ length: 7 }, (_, i) => moment(start).add(i, 'days'));
    }, [year, week]);

    // Fetch branches
    useEffect(() => {
        setIsBranchLoading(true);
        getBranches()
            .then((res) => {
                if (res?.success && Array.isArray(res.data)) {
                    const branches = res.data.map((w) => ({ id: w.id, name: w.branchShortName }));
                    setBranchList(branches);
                    const dash = JSON.parse(localStorage.getItem('_dashboard_') || '{}');
                    const match = branches.find((b) => b.name === dash.BranchPartReq);
                    setBranchID(match?.id || branches[0]?.id || '');
                }
            })
            .catch(() => {
                showToast('Failed to load branches', 'error');
                setIsLoading(false);
            })
            .finally(() => {
                setIsBranchLoading(false);
            });
    }, []);

    // Fetch technicians
    useEffect(() => {
        if (!branchID) return;
        setIsTechLoading(true);
        getWorkshopShiftsTechniciansByBranch(branchID)
            .then((res) => {
                const list = res?.data?.technicians || res?.data;
                if (res?.success && Array.isArray(list)) {
                    setTechList(
                        list.map((t) => ({
                            id: t.technicianUserID || t.id || t.userId,
                            name: t.technicianUserName || t.name || t.userName || t.description
                        }))
                    );
                } else {
                    setTechList([]);
                }
            })
            .catch(() => setTechList([]))
            .finally(() => {
                setIsTechLoading(false);
            });
    }, [branchID]);

    // Load grid data
    const loadData = async () => {
        if (!branchID || !year || !week) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const res = await getWorkshopShiftWeeklyData(branchID, year, week);
            if (res?.success) {
                setShifts(res.data?.shift || []);
                setWeeklyData(res.data?.weeklyData || []);
            } else {
                setShifts([]);
                setWeeklyData([]);
                showToast(res.data?.message || res.message || 'Failed to load schedules', 'error');
            }
        } catch {
            showToast('Failed to load schedules', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [branchID, year, week]);

    // Build per-shift grid data
    const shiftsGrid = useMemo(() => {
        const activeShiftIDs = new Set(weeklyData.map((r) => r.shiftID));
        const activeShifts = shifts.filter((s) => activeShiftIDs.has(s.shiftID));

        return activeShifts.map((shift) => {
            const shiftRows = weeklyData.filter((r) => r.shiftID === shift.shiftID);

            const daysData = daysOfWeek.map((day) => {
                const ds = day.format('YYYY-MM-DD');
                const items = shiftRows.filter((r) => moment(r.shiftDate).format('YYYY-MM-DD') === ds);
                const isScheduled = items.length > 0;
                return {
                    day,
                    isScheduled,
                    scheduleID: items[0]?.shiftScheduleID || null,
                    maxTechs: items[0]?.maxTechniciansRequired || 0,
                    assignments: items.filter((r) => r.technicianUserID)
                };
            });

            const maxSlots = Math.max(0, ...daysData.map((d) => d.maxTechs));
            return { ...shift, daysData, maxSlots };
        });
    }, [shifts, weeklyData, daysOfWeek]);

    // Open calendar modal on cell click
    const openCalendarFor = (dayObj, shift) => {
        if (dayObj.isScheduled) {
            setSelectedSchedule({
                shiftID: shift.shiftID,
                shiftDate: dayObj.day.toDate(),
                maxTechniciansRequired: dayObj.maxTechs,
                shiftScheduleID: dayObj.scheduleID
            });
        } else {
            setSelectedSchedule({
                shiftID: shift.shiftID,
                shiftDate: dayObj.day.toDate(),
                maxTechniciansRequired: 2,
                shiftScheduleID: null
            });
        }
        setCalendarOpen(true);
    };

    // Derive branch name from current selection
    const branchName = branchList.find((b) => String(b.id) === String(branchID))?.name || '';

    // Open assign modal — triggered from an empty slot
    const openAssignDay = (dayObj, shift) => {
        setAssignParams({
            shiftID: shift.shiftID,
            shiftName: shift.shiftName,
            startDate: dayObj.day.toDate(),
            endDate: dayObj.day.toDate()
        });
        setAssignOpen(true);
    };

    // Open unassign modal — triggered from a filled tech chip
    const openUnassignDay = (dayObj, assignment, shift) => {
        setUnassignParams({
            shiftID: shift.shiftID,
            shiftName: shift.shiftName,
            startDate: dayObj.day.toDate(),
            endDate: dayObj.day.toDate(),
            assignment
        });
        setUnassignOpen(true);
    };

    // Open holiday modal — triggered from the menu option
    const openHolidayDay = (dayObj, assignment, shift) => {
        setHolidayParams({
            shiftID: shift.shiftID,
            shiftName: shift.shiftName,
            startDate: dayObj.day.toDate(),
            endDate: dayObj.day.toDate(),
            assignment
        });
        setHolidayOpen(true);
    };

    const closeCalendar = (refresh) => {
        setCalendarOpen(false);
        setSelectedSchedule(null);
        if (refresh === true) loadData();
    };

    const closeAssign = (refresh) => {
        setAssignOpen(false);
        setAssignParams(null);
        if (refresh === true) loadData();
    };

    const closeUnassign = (refresh) => {
        setUnassignOpen(false);
        setUnassignParams(null);
        if (refresh === true) loadData();
    };

    const closeHoliday = (refresh) => {
        setHolidayOpen(false);
        setHolidayParams(null);
        if (refresh === true) loadData();
    };

    const handleMenuOpen = (event, dayObj, assignment, shift) => {
        event.stopPropagation();
        setMenuAnchor(event.currentTarget);
        setActiveMenuAssignment({ dayObj, assignment, shift });
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setActiveMenuAssignment(null);
    };

    const handleMenuUnassign = () => {
        if (activeMenuAssignment) {
            const { dayObj, assignment, shift } = activeMenuAssignment;
            openUnassignDay(dayObj, assignment, shift);
        }
        handleMenuClose();
    };

    const handleMenuHoliday = () => {
        if (activeMenuAssignment) {
            const { dayObj, assignment, shift } = activeMenuAssignment;
            openHolidayDay(dayObj, assignment, shift);
        }
        handleMenuClose();
    };

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <div className={classes.root}>
            <BreadCrumbs crumbs={crumbs} />

            {/* ── Toolbar ── */}
            <div className={classes.topBar}>
                <div className={classes.filterWrap}>
                    <span className={classes.filterLabel} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        Branch
                        {isBranchLoading && <CircularProgress size={10} />}
                    </span>
                    <SelectBox
                        name="branchID"
                        value={branchID}
                        onChange={(e) => setBranchID(e.target.value)}
                        List={branchList}
                        insertEmpty={false}
                        style={{ minWidth: 160 }}
                        disabled={isBranchLoading}
                    />
                </div>

                <div className={classes.filterWrap}>
                    <span className={classes.filterLabel}>Week</span>
                    <SelectBox
                        name="week"
                        value={week}
                        onChange={(e) => setWeek(parseInt(e.target.value, 10))}
                        List={weekList}
                        insertEmpty={false}
                        style={{ minWidth: 230 }}
                    />
                </div>

                <div className={classes.filterWrap}>
                    <span className={classes.filterLabel}>Year</span>
                    <SelectBox
                        name="year"
                        value={year}
                        onChange={(e) => setYear(parseInt(e.target.value, 10))}
                        List={yearList}
                        insertEmpty={false}
                        style={{ minWidth: 90 }}
                    />
                </div>

                <Button
                    variant="contained"
                    className={classes.createBtn}
                    startIcon={<AddIcon style={{ fontSize: 16 }} />}
                    onClick={() => {
                        setSelectedSchedule(null);
                        setCalendarOpen(true);
                    }}
                >
                    Create Calendar
                </Button>
            </div>

            {/* ── Table / Content Loader ── */}
            {isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <CircularProgress size={40} />
                </div>
            ) : (
                <div className={classes.tableWrap}>
                    <TableContainer>
                        <Table size="small" style={{ tableLayout: 'fixed', minWidth: 900 }}>
                            {/* ── Head ── */}
                            <TableHead>
                                <TableRow>
                                    {daysOfWeek.map((day, idx) => (
                                        <HC key={day.format('YYYY-MM-DD')} align="center" style={{ width: 130, padding: '8px 10px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                                                {idx === 0 && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={handlePrevWeek}
                                                        style={{
                                                            position: 'absolute',
                                                            left: -4,
                                                            padding: 4,
                                                            color: '#3B82F6',
                                                            backgroundColor: '#EFF6FF',
                                                            border: '1px solid #BFDBFE'
                                                        }}
                                                        title="Previous Week"
                                                    >
                                                        <ChevronLeftIcon style={{ fontSize: 16 }} />
                                                    </IconButton>
                                                )}
                                                <div style={{ flex: 1, textAlign: 'center' }}>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{day.format('DD/MM/YYYY')}</div>
                                                    <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 1, textTransform: 'uppercase' }}>
                                                        {day.format('dddd')}
                                                    </div>
                                                </div>
                                                {idx === 6 && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleNextWeek}
                                                        style={{
                                                            position: 'absolute',
                                                            right: -4,
                                                            padding: 4,
                                                            color: '#3B82F6',
                                                            backgroundColor: '#EFF6FF',
                                                            border: '1px solid #BFDBFE'
                                                        }}
                                                        title="Next Week"
                                                    >
                                                        <ChevronRightIcon style={{ fontSize: 16 }} />
                                                    </IconButton>
                                                )}
                                            </div>
                                        </HC>
                                    ))}
                                </TableRow>
                            </TableHead>

                            {/* ── Body ── */}
                            <TableBody>
                                {weeklyData.length === 0 ? (
                                    <TableRow>
                                        <HC component="td" colSpan={7} align="center" style={{ padding: '60px 20px', borderBottom: 'none' }}>
                                            <Typography style={{ fontWeight: 600, color: '#6B7280', fontSize: 14 }}>
                                                No shifts configured for this week.
                                            </Typography>
                                            <Typography style={{ color: '#9CA3AF', fontSize: 12, marginTop: 4 }}>
                                                Click "Create Calendar" to add a schedule.
                                            </Typography>
                                        </HC>
                                    </TableRow>
                                ) : (
                                    shiftsGrid.map((shift) => (
                                        <React.Fragment key={shift.shiftID}>
                                            {/* Daily scheduling row */}
                                            <TableRow>
                                                {shift.daysData.map((dayObj, idx) => (
                                                    <HC component="td" key={idx} align="center" style={{ padding: '5px 8px' }}>
                                                        {dayObj.isScheduled ? (
                                                            <div
                                                                className={classes.shiftPill}
                                                                style={{
                                                                    backgroundColor: shift.bgcolor || '#EFF6FF',
                                                                    color: shift.txtcolor || '#2563EB',
                                                                    borderColor: `${shift.txtcolor || '#2563EB'}40`,
                                                                    width: '100%',
                                                                    boxSizing: 'border-box'
                                                                }}
                                                                onClick={() => openCalendarFor(dayObj, shift)}
                                                            >
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                    {shift.shiftName}
                                                                    <EditIcon style={{ fontSize: 10 }} />
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div
                                                                className={classes.emptyCell}
                                                                onClick={() => openCalendarFor(dayObj, shift)}
                                                                title="Click to schedule"
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: '#D1D5DB',
                                                                    width: '90%',
                                                                    margin: '0 auto',
                                                                    boxSizing: 'border-box',
                                                                    transition: 'color 0.15s, background 0.15s'
                                                                }}
                                                                onMouseOver={(e) => {
                                                                    e.currentTarget.style.color = '#3B82F6';
                                                                }}
                                                                onMouseOut={(e) => {
                                                                    e.currentTarget.style.color = '#D1D5DB';
                                                                }}
                                                            >
                                                                <AddIcon style={{ fontSize: 16 }} />
                                                            </div>
                                                        )}
                                                    </HC>
                                                ))}
                                            </TableRow>

                                            {/* Technician slot rows */}
                                            {Array.from({ length: shift.maxSlots }).map((_, slotIdx) => {
                                                return (
                                                    <TableRow key={slotIdx} className={classes.techRow}>
                                                        {/* Day columns */}
                                                        {shift.daysData.map((dayObj, dayIdx) => {
                                                            const available = dayObj.isScheduled && slotIdx < dayObj.maxTechs;
                                                            const assignment = dayObj.assignments[slotIdx];
                                                            return (
                                                                <HC component="td" key={dayIdx} align="center" style={{ padding: '5px 8px' }}>
                                                                    {available ? (
                                                                        assignment ? (
                                                                            <div
                                                                                className={classes.techChip}
                                                                                title={assignment.technicianUserName + (assignment.isOnHoliday ? ' (On Holiday)' : '')}
                                                                                style={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'space-between',
                                                                                    paddingRight: '0px',
                                                                                    gap: '4px',
                                                                                    width: '90%',
                                                                                    margin: '0 auto',
                                                                                    boxSizing: 'border-box',
                                                                                    height: 28,
                                                                                    ...(assignment.isOnHoliday ? {
                                                                                        backgroundColor: '#FEE2E2',
                                                                                        color: '#DC2626',
                                                                                        borderColor: '#FCA5A5'
                                                                                    } : {})
                                                                                }}
                                                                            >
                                                                                <div
                                                                                    style={{
                                                                                        overflow: 'hidden',
                                                                                        textOverflow: 'ellipsis',
                                                                                        whiteSpace: 'nowrap',
                                                                                        textAlign: 'left',
                                                                                        flex: 1,
                                                                                        paddingLeft: '8px'
                                                                                    }}
                                                                                >
                                                                                    {formatTechName(
                                                                                        assignment.technicianUserID,
                                                                                        assignment.technicianUserName,
                                                                                        technicianList
                                                                                    )}
                                                                                </div>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    style={{ padding: 4, color: assignment.isOnHoliday ? '#EF4444' : '#9CA3AF', flexShrink: 0 }}
                                                                                    onClick={(e) => handleMenuOpen(e, dayObj, assignment, shift)}
                                                                                >
                                                                                    <MoreVertIcon style={{ fontSize: 14 }} />
                                                                                </IconButton>
                                                                            </div>
                                                                        ) : (
                                                                            <div
                                                                                style={{
                                                                                    height: 28,
                                                                                    width: '90%',
                                                                                    margin: '0 auto',
                                                                                    borderRadius: 4,
                                                                                    cursor: 'pointer',
                                                                                    border: '1px dashed #D1D5DB',
                                                                                    backgroundColor: '#FCFDFD',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    color: '#9CA3AF',
                                                                                    fontSize: '11px',
                                                                                    fontWeight: 500,
                                                                                    boxSizing: 'border-box',
                                                                                    transition: 'all 0.15s'
                                                                                }}
                                                                                onClick={() => openAssignDay(dayObj, shift)}
                                                                                onMouseOver={(e) => {
                                                                                    e.currentTarget.style.backgroundColor = '#EFF6FF';
                                                                                    e.currentTarget.style.borderColor = '#3B82F6';
                                                                                    e.currentTarget.style.color = '#2563EB';
                                                                                }}
                                                                                onMouseOut={(e) => {
                                                                                    e.currentTarget.style.backgroundColor = '#FCFDFD';
                                                                                    e.currentTarget.style.borderColor = '#D1D5DB';
                                                                                    e.currentTarget.style.color = '#9CA3AF';
                                                                                }}
                                                                            >
                                                                                + Assign
                                                                            </div>
                                                                        )
                                                                    ) : (
                                                                        <div
                                                                            style={{
                                                                                height: 28,
                                                                                width: '90%',
                                                                                margin: '0 auto',
                                                                                borderRadius: 4,
                                                                                backgroundColor: '#F3F4F6',
                                                                                border: '1px solid #E5E7EB',
                                                                                boxSizing: 'border-box',
                                                                                opacity: 0.6,
                                                                                cursor: 'not-allowed'
                                                                            }}
                                                                            title="Not scheduled / required"
                                                                        />
                                                                    )}
                                                                </HC>
                                                            );
                                                        })}
                                                    </TableRow>
                                                );
                                            })}
                                        </React.Fragment>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            )}

            {/* ── Modals ── */}
            {calendarOpen && (
                <DialogComp
                    title={selectedSchedule ? (selectedSchedule.shiftScheduleID ? 'Edit Shift Schedule' : 'Add Shift Schedule') : 'Create Shift Schedule'}
                    onClose={closeCalendar}
                    maxWidth="sm"
                    fullWidth
                >
                    <CreateCalendarModal
                        shiftList={shifts.map((s) => ({ id: s.shiftID, name: s.shiftName }))}
                        branchID={branchID}
                        branchName={branchName}
                        defaultStartDate={selectedSchedule?.shiftDate || daysOfWeek[0].toDate()}
                        defaultEndDate={selectedSchedule?.shiftDate || daysOfWeek[6].toDate()}
                        existingSchedule={selectedSchedule}
                        onClose={closeCalendar}
                    />
                </DialogComp>
            )}

            {assignOpen && assignParams && (
                <DialogComp title="Assign Technician" onClose={closeAssign} maxWidth="sm" fullWidth>
                    <AssignTechnicianModal
                        technicianList={technicianList}
                        isTechLoading={isTechLoading}
                        shiftID={assignParams.shiftID}
                        shiftName={assignParams.shiftName}
                        branchName={branchName}
                        branchID={branchID}
                        defaultStartDate={assignParams.startDate}
                        defaultEndDate={assignParams.endDate}
                        onClose={closeAssign}
                    />
                </DialogComp>
            )}

            {unassignOpen && unassignParams && (
                <DialogComp title="Unassign Technician" onClose={closeUnassign} maxWidth="sm" fullWidth>
                    <UnassignTechnicianModal
                        shiftID={unassignParams.shiftID}
                        shiftName={unassignParams.shiftName}
                        branchName={branchName}
                        branchID={branchID}
                        defaultStartDate={unassignParams.startDate}
                        defaultEndDate={unassignParams.endDate}
                        existingAssignment={unassignParams.assignment}
                        technicianName={formatTechName(
                            unassignParams.assignment?.technicianUserID,
                            unassignParams.assignment?.technicianUserName,
                            technicianList
                        )}
                        onClose={closeUnassign}
                    />
                </DialogComp>
            )}

            {holidayOpen && holidayParams && (
                <DialogComp title="Add Holiday" onClose={closeHoliday} maxWidth="sm" fullWidth>
                    <AddHolidayModal
                        shiftID={holidayParams.shiftID}
                        shiftName={holidayParams.shiftName}
                        branchName={branchName}
                        branchID={branchID}
                        defaultStartDate={holidayParams.startDate}
                        defaultEndDate={holidayParams.endDate}
                        existingAssignment={holidayParams.assignment}
                        technicianName={formatTechName(
                            holidayParams.assignment?.technicianUserID,
                            holidayParams.assignment?.technicianUserName,
                            technicianList
                        )}
                        onClose={closeHoliday}
                    />
                </DialogComp>
            )}

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                keepMounted
                getContentAnchorEl={null}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
            >
                <MenuItem onClick={handleMenuUnassign} style={{ fontSize: 13 }}>
                    Unassign
                </MenuItem>
                {!activeMenuAssignment?.assignment?.isOnHoliday && (
                    <MenuItem onClick={handleMenuHoliday} style={{ fontSize: 13 }}>
                        Holiday
                    </MenuItem>
                )}
            </Menu>
        </div>
    );
};

export default WorkshopShiftSchedule;
